import React, { useMemo } from 'react';
import ChurchMonthCalendar from './ChurchMonthCalendar';
import { UPCOMING_EVENT_CARDS } from '../../data/churchEvents';
import { CHURCH_CONTACT } from '../../data/churchContact';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import './ChurchCalendarSection.css';

const customEventToCard = (event) => ({
  id: event.id,
  name: event.title,
  time: event.time || event.detailLines?.[0] || null,
  location: event.note || event.detailLines?.[1] || null,
  until: event.end || event.start,
});

/** Dated events drop off the list once they have passed; recurring events always stay. */
const isUpcoming = (card, todayIso) => !card.until || card.until >= todayIso;

const ChurchCalendarSection = ({
  className = '',
  title,
  titleClassName = 'section-title',
  sectionProps = {},
}) => {
  const { isEditing } = useAuth();
  const { getContent } = useContent();
  const customEvents = getContent('calendar.customEvents', []);

  const todayIso = useMemo(() => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }, []);

  const heading = title ?? `${new Date().getFullYear()} Church Calendar`;

  const upcomingEvents = useMemo(() => {
    const customCards = (Array.isArray(customEvents) ? customEvents : []).map(customEventToCard);
    return [...UPCOMING_EVENT_CARDS, ...customCards]
      .filter((card) => isUpcoming(card, todayIso))
      .sort((a, b) => {
        if (!a.until && !b.until) return 0;
        if (!a.until) return 1;
        if (!b.until) return -1;
        return a.until.localeCompare(b.until);
      });
  }, [customEvents, todayIso]);

  return (
  <div className={`church-calendar-section ${className}`.trim()} {...sectionProps}>
    <span className="editor-field-anchor" data-content-key="calendar.customEvents" aria-hidden="true" />
    <h2 className={titleClassName}>
      {heading}
      <div className="divider" />
    </h2>
    <div className="church-calendar-layout">
      <div className="church-calendar-main">
        <ChurchMonthCalendar />
      </div>
      <aside className="church-calendar-events" aria-label="Upcoming events">
        <h3 className="church-calendar-events-title">Upcoming Events</h3>
        {isEditing && (
          <p className="church-calendar-events-hint">
            This list is built from the church calendar on the left. Use <strong>+ Add event</strong> on the
            calendar to add or change what appears here.
          </p>
        )}
        <ul className="church-calendar-events-list">
          {upcomingEvents.map((event) => (
            <li key={event.id} className="service-item">
              <h4 className="day">{event.name}</h4>
              <p className="time-details">
                {event.time && (
                  <>
                    <span className="time">{event.time}</span>
                    <br />
                  </>
                )}
                {event.location && <span className="location">{event.location}</span>}
              </p>
            </li>
          ))}
        </ul>
        <div className="church-calendar-events-contact">
          <p className="church-calendar-events-contact-lead">For more details, contact us:</p>
          <ul className="church-calendar-events-contact-list">
            <li>
              <a href={`mailto:${CHURCH_CONTACT.email}`} className="church-calendar-events-contact-link">
                <i className="fas fa-envelope" aria-hidden="true" />
                {CHURCH_CONTACT.email}
              </a>
            </li>
            <li>
              <a href={`tel:${CHURCH_CONTACT.phoneTel}`} className="church-calendar-events-contact-link">
                <i className="fas fa-phone" aria-hidden="true" />
                {CHURCH_CONTACT.phone}
              </a>
              <span className="church-calendar-events-contact-note"> ({CHURCH_CONTACT.pastorName})</span>
            </li>
          </ul>
          <div className="church-calendar-events-social">
            <a
              href={CHURCH_CONTACT.social.facebook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="church-calendar-events-social-link"
              aria-label={CHURCH_CONTACT.social.facebook.label}
            >
              <i className="fab fa-facebook" aria-hidden="true" />
            </a>
            <a
              href={CHURCH_CONTACT.social.youtube.url}
              target="_blank"
              rel="noopener noreferrer"
              className="church-calendar-events-social-link"
              aria-label={CHURCH_CONTACT.social.youtube.label}
            >
              <i className="fab fa-youtube" aria-hidden="true" />
            </a>
            <a
              href={CHURCH_CONTACT.social.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="church-calendar-events-social-link"
              aria-label={CHURCH_CONTACT.social.instagram.label}
            >
              <i className="fab fa-instagram" aria-hidden="true" />
            </a>
          </div>
        </div>
      </aside>
    </div>
  </div>
  );
};

export default ChurchCalendarSection;
